// src/components/Footer.tsx
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface FooterProps {
  onNavigate?: (page: string) => void;
}

export function Footer({ onNavigate }: FooterProps) {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  // Only show footer on landing (home) page
  if (location.pathname !== '/') {
    return null;
  }

  const handleNavClick = (page: string) => {
    // Map to EdgeAI routes where appropriate
    switch (page) {
      case 'Home':
      case 'home':
        navigate('/');
        break;
      case 'Courses':
      case 'courses':
        navigate('/courses');
        break;
      case 'Classroom':
      case 'classroom':
        navigate('/classroom');
        break;
      case 'About':
      case 'About Us':
      case 'about':
        navigate('/about');
        break;
      case 'Contact':
      case 'contact':
        navigate('/contact');
        break;
      // Privacy / Terms / Support left as placeholders for now
      case 'Privacy':
      case 'privacy':
          navigate('/privacy');
          break;
      case 'Terms':
      case 'terms':
        navigate('/terms');
          break;
      
      default:
        break;
    }

    if (onNavigate) {
      onNavigate(page);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative mt-auto">
      {/* glowing top line */}
      <div className="pointer-events-none absolute -top-px inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-400/70 to-transparent" />

      <div
        className="border-t border-cyan-400/10 bg-[#050914]/95 backdrop-blur-xl"
        style={{
          boxShadow: '0 -20px 60px rgba(0, 229, 209, 0.20)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-10 md:py-12">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
            {/* Brand Section */}
            <div>
              <h3 className="text-white text-xl mb-3">EdgeAI Academy</h3>
              <p className="text-cyan-100/80 text-sm leading-relaxed">
                End-to-end Edge AI &amp; IoT learning — from sensors to silicon.
                <br />
                <span className="text-cyan-200/70">
                  Powered by MindEngage Inc.
                </span>
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white mb-3 text-sm tracking-wide uppercase">
                Quick Links
              </h4>
              <ul className="space-y-2 text-sm">
                {[
                  { label: 'Home', page: 'Home' },
                  { label: 'Courses', page: 'Courses' },
                  { label: 'Classroom', page: 'Classroom' },
                  { label: 'About Us', page: 'About' },
                  { label: 'Contact', page: 'Contact' },
                ].map((item) => (
                  <li key={item.label}>
                    <button
                      onClick={() => handleNavClick(item.page)}
                      className="relative text-slate-200/80 hover:text-cyan-300 transition-colors"
                    >
                      {item.label}
                      <span className="block h-px w-0 bg-cyan-400/70 mt-0.5 transition-all duration-200 group-hover:w-full" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info (address kept as-is) */}
            <div>
              <h4 className="text-white mb-3 text-sm tracking-wide uppercase">
                Contact
              </h4>
              <div className="text-slate-200/80 text-sm space-y-1">
                <p className="font-medium text-cyan-100">MindEngage Inc</p>
                <p>84 W. Santa Clara St., #700</p>
                <p>San Jose, CA 95113</p>
                <p className="mt-3">
                  <a
                    href="tel:+18669536888"
                    className="text-cyan-300 hover:text-cyan-200 transition-colors"
                  >
                    1 866 953 6888
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 pt-6">
            {/* Bottom Bar */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs md:text-sm text-slate-300/70">
              <div className="text-center md:text-left">
                © {currentYear} MindEngage Inc. All rights reserved.
              </div>
              <div className="flex flex-wrap justify-center gap-6">
                {/* Keep labels/behavior; styling only */}
                <button
                  onClick={() => handleNavClick('Privacy')}
                  className="hover:text-cyan-300 transition-colors"
                >
                  Privacy Policy
                </button>
                <button
                  onClick={() => handleNavClick('Terms')}
                  className="hover:text-cyan-300 transition-colors"
                >
                  Terms of Service
                </button>
                <button
                  onClick={() => handleNavClick('Contact')}
                  className="hover:text-cyan-300 transition-colors"
                >
                  Support
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
